<template>
  <div class="appointment-audit-detail">
    <div class="page-header">
      <div class="page-title-row">
        <h2 class="page-title">{{ pageTitle }}</h2>
        <el-button type="text" class="back-link" @click="goToAuditList">
          <i class="el-icon-arrow-left"></i> 返回列表
        </el-button>
      </div>
    </div>

    <div class="audit-layout">
      <!-- 左侧主内容 -->
      <div class="main-panel">
        <!-- 基本信息 -->
        <section class="panel-section">
          <div class="section-title">基本信息</div>
          <div class="basic-info-grid">
            <div class="info-cell">
              <span class="label">合同编号</span>
              <span class="value">{{ currentAppointment.contractNo || '-' }}</span>
            </div>
            <div class="info-cell">
              <span class="label">联系人员</span>
              <span class="value">{{ currentAppointment.contactPerson || currentAppointment.brokerName || '-' }}</span>
            </div>
            <div class="info-cell">
              <span class="label">{{ warehouseLabel }}</span>
              <span class="value">{{ currentAppointment.warehouseName || currentAppointment.warehouse || '-' }}</span>
            </div>
            <div class="info-cell">
              <span class="label">手机号码</span>
              <span class="value">{{ currentAppointment.contactPhone || currentAppointment.brokerPhone || '-' }}</span>
            </div>
            <div class="info-cell">
              <span class="label">粮食品种</span>
              <span class="value">{{ currentAppointment.grainType || '-' }}</span>
            </div>
            <div class="info-cell">
              <span class="label">{{ dateLabel }}</span>
              <span class="value">{{ formatDate(currentAppointment.enterDate) }}</span>
            </div>
          </div>
        </section>

        <!-- 车辆信息表格 -->
        <section class="panel-section">
          <div class="section-title">预约单车辆信息（批量审核）</div>
          <el-table
            :data="driverTableData"
            border
            stripe
            size="small"
            class="driver-table"
            empty-text="暂无车辆信息"
          >
            <el-table-column label="序号" width="70" align="center">
              <template slot-scope="scope">
                {{ String(scope.$index + 1).padStart(2, '0') }}
              </template>
            </el-table-column>
            <el-table-column label="车牌号码" min-width="120" align="center">
              <template slot-scope="scope">
                <span class="plate-link">{{ scope.row.plateNumber || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="driverName" label="驾驶员" min-width="100" align="center" />
            <el-table-column prop="driverPhone" label="手机号" min-width="120" align="center" />
            <el-table-column label="身份证" min-width="160" align="center">
              <template slot-scope="scope">
                {{ maskIdCard(scope.row.driverIdCard || scope.row.idCard) }}
              </template>
            </el-table-column>
            <el-table-column label="载重(吨)" width="90" align="center">
              <template slot-scope="scope">
                {{ scope.row.loadWeight != null ? scope.row.loadWeight : '-' }}
              </template>
            </el-table-column>
          </el-table>
        </section>

        <!-- 委托书 -->
        <section class="panel-section">
          <div class="section-title">{{ attachmentTitle }}</div>
          <div class="delegation-box">
            <template v-if="attachmentList.length > 0">
              <div class="delegation-images">
                <el-image
                  v-for="(item, index) in attachmentList"
                  :key="index"
                  :src="item.url"
                  :preview-src-list="previewUrls"
                  fit="contain"
                  class="delegation-image"
                >
                  <div slot="error" class="image-slot">
                    <i class="el-icon-picture-outline"></i>
                    <span>加载失败</span>
                  </div>
                </el-image>
              </div>
            </template>
            <div v-else class="delegation-empty">
              <i class="el-icon-picture-outline"></i>
              <span>暂无{{ attachmentTitle }}</span>
            </div>
          </div>
        </section>
      </div>

      <!-- 右侧审核栏 -->
      <div class="side-panel">
        <section class="panel-section audit-panel">
          <div class="section-title">审核操作</div>
          <div class="audit-form-wrap">
            <div class="audit-label">审核意见描述</div>
            <el-input
              v-model="auditForm.remark"
              type="textarea"
              :rows="5"
              placeholder="请输入审核意见，若拒绝申请请详细说明原因..."
              maxlength="500"
              show-word-limit
            />
            <div class="audit-actions">
              <el-button
                class="btn-reject"
                :loading="auditLoading"
                :disabled="currentAppointment.status !== 'pending'"
                @click="submitAudit('rejected')"
              >
                拒绝申请
              </el-button>
              <el-button
                class="btn-approve"
                :loading="auditLoading"
                :disabled="currentAppointment.status !== 'pending'"
                @click="submitAudit('approved')"
              >
                审核通过
              </el-button>
            </div>
          </div>
        </section>

        <section class="panel-section history-panel">
          <div class="section-title">历史审核记录</div>
          <div v-if="sortedAuditLogs.length > 0" class="timeline">
            <div v-for="(log, index) in sortedAuditLogs" :key="index" class="timeline-item">
              <div class="timeline-dot"></div>
              <div class="timeline-content">
                <div class="timeline-title">{{ log.desc || '操作记录' }}</div>
                <div class="timeline-meta">
                  <span>操作人：{{ log.operator || '-' }}</span>
                  <span>{{ formatTime(log.time) }}</span>
                </div>
                <div v-if="log.reason && log.reason !== log.desc" class="timeline-reason">
                  {{ log.reason }}
                </div>
              </div>
            </div>
          </div>
          <el-empty v-else description="暂无审核记录" :image-size="60" />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppointmentAuditDetail',
  data() {
    return {
      currentAppointment: {},
      auditForm: {
        remark: ''
      },
      auditLoading: false
    }
  },
  computed: {
    isInbound() {
      return String(this.currentAppointment.type || '').toLowerCase() === 'in'
    },
    pageTitle() {
      return this.isInbound ? '入库预约单审核' : '出库预约单审核'
    },
    dateLabel() {
      return this.isInbound ? '入库日期' : '出库日期'
    },
    warehouseLabel() {
      return this.isInbound ? '入库仓房' : '装粮仓库'
    },
    attachmentTitle() {
      return this.isInbound ? '入库委托书' : '出库委托书'
    },
    driverTableData() {
      return this.currentAppointment.drivers || []
    },
    attachmentList() {
      const list = this.currentAppointment.attachments || []
      return list.map((item) => ({
        ...item,
        url: this.normalizeUploadUrl(item.url)
      })).filter((item) => item.url)
    },
    previewUrls() {
      return this.attachmentList.map((item) => item.url)
    },
    sortedAuditLogs() {
      const logs = this.currentAppointment.auditLogs || []
      return [...logs].sort((a, b) => new Date(b.time) - new Date(a.time))
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    normalizeUploadUrl(url) {
      if (!url || typeof url !== 'string') return url
      if (/^(data:|blob:|wxfile:)/.test(url)) return url
      if (url.includes('/api/uploads/')) return url
      if (url.startsWith('/uploads/')) return `/api${url}`
      return url.replace(/^(https?:\/\/[^/]+)\/uploads\//, '$1/api/uploads/')
    },
    async loadData() {
      try {
        const id = this.$route.params.id
        const res = await this.$http.get(`/admin/appointment/detail/${id}`)
        if (res.data.code === 200) {
          this.currentAppointment = res.data.data || {}
        } else {
          this.$message.error(res.data.message || '加载详情失败')
        }
      } catch (error) {
        console.error('加载详情失败:', error)
        this.$message.error('加载详情失败，请重试')
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      if (Number.isNaN(date.getTime())) return time
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    },
    maskIdCard(idCard) {
      if (!idCard) return '-'
      if (idCard.length <= 8) return idCard
      return `${idCard.slice(0, 6)}********${idCard.slice(-4)}`
    },
    async submitAudit(result) {
      if (result === 'rejected' && !this.auditForm.remark.trim()) {
        this.$message.warning('拒绝申请时请填写审核意见')
        return
      }
      const actionText = result === 'approved' ? '通过' : '拒绝'
      try {
        await this.$confirm(`确认${actionText}该预约申请？`, '审核确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: result === 'approved' ? 'success' : 'warning'
        })
      } catch (e) {
        return
      }

      this.auditLoading = true
      try {
        let res
        if (result === 'approved') {
          res = await this.$http.post('/admin/appointment/approve', {
            id: this.currentAppointment.id,
            reason: this.auditForm.remark
          })
        } else {
          res = await this.$http.post('/admin/appointment/reject', {
            id: this.currentAppointment.id,
            reason: this.auditForm.remark
          })
        }
        if (res.data.code === 200) {
          this.$message.success(result === 'approved' ? '审核通过' : '已拒绝申请')
          this.goToAuditList()
        } else {
          this.$message.error(res.data.message || '审核失败')
        }
      } catch (e) {
        console.error('审核失败:', e)
        this.$message.error('审核失败，请重试')
      } finally {
        this.auditLoading = false
      }
    },
    goToAuditList() {
      this.$router.push('/appointment-audit')
    }
  }
}
</script>

<style scoped>
.appointment-audit-detail {
  padding: 0 4px 24px;
  background: #f5f7fa;
  min-height: calc(100vh - 100px);
}

.page-header {
  margin-bottom: 16px;
}

.page-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2d3d;
}

.back-link {
  font-size: 14px;
  color: #606266;
  padding: 0;
}

.audit-layout {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.main-panel {
  flex: 1;
  min-width: 0;
}

.side-panel {
  width: 320px;
  flex-shrink: 0;
}

.panel-section {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 16px;
  overflow: hidden;
}

.section-title {
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
}

.basic-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  padding: 8px 0 16px;
}

.info-cell {
  padding: 14px 20px 0;
}

.info-cell .label {
  display: block;
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.info-cell .value {
  display: block;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  word-break: break-all;
}

.driver-table {
  margin: 0;
}

.driver-table ::v-deep .el-table__header th {
  background: #fafbfc;
  color: #606266;
  font-weight: 600;
}

.plate-link {
  color: #409eff;
  font-weight: 500;
}

.delegation-box {
  min-height: 220px;
  padding: 20px;
  background: #f9fafb;
}

.delegation-images {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.delegation-image {
  width: 100%;
  max-width: 480px;
  height: 280px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.delegation-image ::v-deep .el-image__inner {
  object-fit: contain;
}

.image-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #c0c4cc;
  font-size: 13px;
}

.image-slot i {
  font-size: 40px;
  margin-bottom: 8px;
}

.delegation-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  color: #c0c4cc;
  font-size: 14px;
}

.delegation-empty i {
  font-size: 48px;
  margin-bottom: 12px;
}

.audit-panel .audit-form-wrap {
  padding: 16px 20px 20px;
}

.audit-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 10px;
}

.audit-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.audit-actions .el-button {
  flex: 1;
  height: 44px;
  font-size: 15px;
  font-weight: 500;
  border: none;
  color: #fff;
}

.btn-reject {
  background: #f56c6c;
}

.btn-reject:hover,
.btn-reject:focus {
  background: #f78989;
  color: #fff;
}

.btn-approve {
  background: #67c23a;
}

.btn-approve:hover,
.btn-approve:focus {
  background: #85ce61;
  color: #fff;
}

.history-panel {
  margin-bottom: 0;
}

.timeline {
  padding: 16px 20px 20px;
}

.timeline-item {
  position: relative;
  padding-left: 20px;
  padding-bottom: 20px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-item::before {
  content: '';
  position: absolute;
  left: 4px;
  top: 10px;
  bottom: 0;
  width: 1px;
  background: #e4e7ed;
}

.timeline-item:last-child::before {
  display: none;
}

.timeline-dot {
  position: absolute;
  left: 0;
  top: 4px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #409eff;
  border: 2px solid #d9ecff;
}

.timeline-title {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 6px;
}

.timeline-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #909399;
}

.timeline-reason {
  margin-top: 6px;
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
}

@media (max-width: 1100px) {
  .audit-layout {
    flex-direction: column;
  }

  .side-panel {
    width: 100%;
  }

  .basic-info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .basic-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
